/**
 * This Layout is needed for Starter Kit.
 */
import { JSX } from 'react';
import Head from 'next/head';
import { useEffect } from 'react';

import {
  Field,
  ImageField,
  LinkField,
  Placeholder,
  TextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import localFont from 'next/font/local';
import { SitecorePageProps } from 'lib/page-props';
import { SearchMeta, SearchMetaFields } from './shared/SearchMeta';
import Scripts from 'src/Scripts';
import { DEFAULT_THEME, ThemeProvider } from '@ahc-digital/component-library';
import { useCurrentSiteUrls } from './shared/useCurrentSiteUrls';
import { DOCTOR_PAGE_TEMPLATE_ID, LOCATION_PAGE_TEMPLATE_ID } from './shared/static';
import { SparkleMeta, SparkleMetaFields } from './shared/SparkleMeta';

type LayoutProps = Pick<SitecorePageProps, 'layoutData' | 'headLinks'>;

interface RouteFields extends SearchMetaFields {
  [key: string]: unknown;
  Title?: Field;
  BrowserTitle?: TextField;
  CanonicalUrl?: LinkField;
  MetaKeywords?: TextField;
  MetaDescription?: TextField;
  MetaPageType?: TextField;
  OpenGraphTitle?: TextField;
  OpenGraphDescription?: TextField;
  OpenGraphImageUrl?: ImageField;
  OpenGraphType?: TextField;
  OpenGraphSiteName?: TextField;
  OpenGraphAdmins?: TextField;
  OpenGraphAppId?: TextField;
  NoIndex?: Field;
  NoFollow?: Field;
}

/* Font definitions */
const fontMontserrat = localFont({
  src: [
    {
      path: '../node_modules/@ahc-digital/component-library/dist/fonts/Montserrat-VariableFont_wght.ttf',
      weight: '400 600 700',
      style: 'normal',
    },
    {
      path: '../node_modules/@ahc-digital/component-library/dist/fonts/Montserrat-Italic-VariableFont_wght.ttf',
      weight: '400 600 700',
      style: 'italic',
    },
  ],
  variable: '--font-family-default',
});

const fontKnu = localFont({
  src: [
    {
      path: '../node_modules/@ahc-digital/component-library/dist/fonts/KNU_0.woff',
    },
  ],
  variable: '--font-family-knu',
});

const fontZawgyi = localFont({
  src: [
    {
      path: '../node_modules/@ahc-digital/component-library/dist/fonts/zawgyione.woff',
    },
  ],
  variable: '--font-family-zawgyi',
});

const Layout = ({ layoutData, headLinks }: LayoutProps): JSX.Element => {
  const { route, context } = layoutData?.sitecore ?? {};
  const { currentPageUrl, currentSiteBaseUrl } = useCurrentSiteUrls();
  const fields = route?.fields as RouteFields;
  const siteTheme = layoutData?.sitecore?.context?.siteTheme?.value ?? DEFAULT_THEME;
  const appleTouchIcons = layoutData?.sitecore?.context?.appleTouchIcons?.value;
  let robotsStr = 'index, follow';
  if (fields?.NoIndex?.value) robotsStr = robotsStr.replace('index', 'noindex');
  if (fields?.NoFollow?.value) robotsStr = robotsStr.replace('follow', 'nofollow');

  useEffect(() => {
    document?.body?.setAttribute('data-theme', siteTheme);
  }, [siteTheme]);

  //Canonical Url
  let canonicalUrl = fields?.CanonicalUrl?.value?.href;
  if (!canonicalUrl) {
    canonicalUrl = currentPageUrl;
  } else {
    if (fields?.CanonicalUrl?.value?.linktype === 'internal') {
      canonicalUrl = `${currentSiteBaseUrl}${canonicalUrl}`;
    }
  }

  //Og Image
  let ogImage = fields?.OpenGraphImageUrl?.value?.src;
  if (!ogImage) {
    ogImage = '';
  } else {
    ogImage = `${currentSiteBaseUrl}${fields?.OpenGraphImageUrl?.value?.src}`;
  }

  //Icon Url
  const iconUrl = `${context?.favicon?.value}`;

  //Og Locale
  let ogLocale = context?.language;
  if (ogLocale === 'en') {
    ogLocale = 'en_US';
  }

  const pageTemplate = route?.templateId;

  const shouldExcludeCanonicalUrl =
    pageTemplate !== DOCTOR_PAGE_TEMPLATE_ID && pageTemplate !== LOCATION_PAGE_TEMPLATE_ID;

  return (
    <>
      <Scripts />
      <Head>
        <title>{fields?.BrowserTitle?.value?.toString() || 'Page'}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        <meta name="keywords" content={fields?.MetaKeywords?.value?.toString()} key="keywords" />
        <meta
          name="description"
          content={fields?.MetaDescription?.value?.toString()}
          key="description"
        />
        <meta name="pagetype" content={fields?.MetaPageType?.value?.toString()} key="type" />
        <meta name="robots" content={robotsStr} />
        <meta
          property="og:title"
          content={fields?.OpenGraphTitle?.value?.toString()}
          key="og:title"
        />
        <meta property="og:type" content={fields?.OpenGraphType?.value?.toString()} key="type" />
        <meta
          property="og:description"
          content={fields?.OpenGraphDescription?.value?.toString()}
          key="og:description"
        />
        <meta property="og:image" content={ogImage} key="image" />
        {shouldExcludeCanonicalUrl && (
          <meta property="og:url" content={canonicalUrl} key="canonical" />
        )}
        <meta property="og:site_name" content={context?.friendlySiteName?.value} key="site_name" />
        <meta property="og:locale" content={ogLocale} />
        <meta itemProp="name" content={context?.friendlySiteName?.value} />
        {shouldExcludeCanonicalUrl && <link rel="canonical" href={canonicalUrl} key="canonical" />}
        <link rel="icon" href={iconUrl} />

        {appleTouchIcons
          ?.filter((icon): icon is { url: string; size?: string } => Boolean(icon?.url))
          .map((icon) => (
            <link
              rel="apple-touch-icon"
              href={icon.url}
              key={icon.url}
              sizes={icon.size?.replaceAll(' ', '')}
            />
          ))}

        {headLinks?.map((headLink) => (
          <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
        ))}
      </Head>
      <style jsx global>{`
        :root {
          --font-family-default: ${fontMontserrat.style.fontFamily};
          --font-family-knu: ${fontKnu.style.fontFamily};
          --font-family-zawgyi: ${fontZawgyi.style.fontFamily};
        }
      `}</style>
      <SearchMeta
        fields={fields as SearchMetaFields}
        route={route ?? undefined}
        layoutData={layoutData ?? undefined}
        publicUrl={currentSiteBaseUrl}
      />

      <SparkleMeta fields={fields as SparkleMetaFields} />

      {/* root placeholder for the app, which we add components to using route data*/}
      <ThemeProvider theme={siteTheme}>
        <>
          {route && <Placeholder name="alert-above-nav" rendering={route} />}
          {route && <Placeholder name="headless-header" rendering={route} />}
          <main>{route && <Placeholder name="headless-main" rendering={route} />}</main>
          {route && <Placeholder name="headless-footer" rendering={route} />}
        </>
      </ThemeProvider>
    </>
  );
};

export default Layout;
